import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Save, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { assetContentUrl, useBranding, useUploadAsset } from "@/hooks/use-branding";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { configKeys } from "@/lib/query";
import { ASSET_SLUGS, GUIDE_CONTENT_TYPES, GUIDE_MAX_BYTES } from "@/schemas/branding";
import { type ParamUploadPdf, paramUploadPdfSchema } from "@/schemas/config";

const GUIDE_ACCEPT = GUIDE_CONTENT_TYPES.join(",");
const GUIDE_MAX_MB = Math.round(GUIDE_MAX_BYTES / 1_000_000);

// The global help guide opened by the question-mark icon in the top bar.
// Stored as an uploadable asset (not on the Param row); shown here because
// "Upload PDFs" is the natural home for admin-managed documents.
function UserGuideSection() {
  const guideInputRef = useRef<HTMLInputElement>(null);
  const { data: branding } = useBranding();
  const uploadMutation = useUploadAsset();
  const guide = branding?.userGuide ?? null;

  function onPick(file: File | undefined) {
    if (!file) return;
    uploadMutation.mutate({ slug: ASSET_SLUGS.userGuide, file });
    if (guideInputRef.current) guideInputRef.current.value = "";
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <h2 className="font-semibold text-lg">User Guide (question-mark help)</h2>
        <p className="text-muted-foreground text-sm">
          The PDF opened by the <span className="font-medium">?</span> icon in the top bar. Upload a
          replacement here (PDF only, max {GUIDE_MAX_MB} MB). Until you do, the icon opens the
          built-in guide.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3">
          {guide ? (
            <p className="text-muted-foreground text-xs">
              Current file: <span className="font-medium">{guide.fileName}</span>
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">
              Using the built-in guide (no upload yet).
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={guideInputRef}
              type="file"
              accept={GUIDE_ACCEPT}
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
            />
            <Button
              onClick={() => guideInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              <Upload className="size-4" />
              {uploadMutation.isPending ? "Uploading..." : guide ? "Replace guide" : "Upload guide"}
            </Button>
            <Button variant="outline" asChild>
              <a href={assetContentUrl(ASSET_SLUGS.userGuide, { download: true })}>
                <Download className="size-4" />
                Download current
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type UploadPdfEdits = Partial<
  Pick<ParamUploadPdf, "claimTeamHistoryHelpFile" | "claimTeamHistoryHelpFileName">
>;

export function UploadPdfPage() {
  const queryClient = useQueryClient();
  // Edits overlay rather than a synced copy: form values are derived
  // (edits.field ?? param.field) on every render, so there's no transient
  // state where the query has resolved but the form hasn't synced — avoids
  // the post-load skeleton flicker.
  const [edits, setEdits] = useState<UploadPdfEdits>({});

  const {
    data: param,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: configKeys.paramUploadPdf,
    queryFn: () => apiGet("/api/config/param/upload-pdf", paramUploadPdfSchema),
  });

  const saveMutation = useMutation({
    mutationFn: (data: {
      claimTeamHistoryHelpFile: string;
      claimTeamHistoryHelpFileName: string;
    }) => {
      if (!param) throw new Error("Param row not loaded");
      return apiSend("PUT", `/api/config/param/${param.id}`, data, z.unknown());
    },
    onSuccess: () => {
      toast.success("PDF configuration saved");
      setEdits({});
      // Both Workflows and Upload PDFs project the same singleton Param row,
      // so invalidate the shared parent key — keeps the two views in sync.
      queryClient.invalidateQueries({ queryKey: configKeys.paramAll });
    },
    onError: (error) => {
      logger.error("upload_pdf_save_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to save configuration");
    },
  });

  if (isLoading) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Upload PDF</h1>
        <div className="mt-4 space-y-3">
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-9 w-24 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (isError) {
    logger.error("Failed to load Upload PDF configuration", { error });
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Upload PDF</h1>
        <ErrorState message="Couldn't load configuration." onRetry={() => refetch()} />
      </div>
    );
  }

  if (!param) return null;

  const form = {
    claimTeamHistoryHelpFile: edits.claimTeamHistoryHelpFile ?? param.claimTeamHistoryHelpFile,
    claimTeamHistoryHelpFileName:
      edits.claimTeamHistoryHelpFileName ?? param.claimTeamHistoryHelpFileName,
  };
  const isDirty =
    form.claimTeamHistoryHelpFile !== param.claimTeamHistoryHelpFile ||
    form.claimTeamHistoryHelpFileName !== param.claimTeamHistoryHelpFileName;

  function handleSave() {
    if (!isDirty) {
      toast.info("No changes to save");
      return;
    }
    saveMutation.mutate(form);
  }

  return (
    <div>
      <div className="flex items-center justify-between rounded-md border bg-card p-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Upload PDF</h1>
          <p className="mt-1 text-muted-foreground">
            Manage the app's help documents: the global user guide (question-mark icon) and the help
            file shown on the Claim Team History page.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="size-4" />
          {saveMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <UserGuideSection />

      <Card className="mt-6">
        <CardHeader>
          <h2 className="font-semibold text-lg">Claim Team History help file</h2>
          <p className="text-muted-foreground text-sm">
            The help PDF linked from the Claim Team History page. These two fields are a label and a
            location — they don't upload a file; an admin points them at an existing PDF.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6">
            <div className="grid gap-1.5">
              <label className="font-medium text-sm" htmlFor="claim-team-history-help-file-name">
                Help File Name
              </label>
              <p className="text-muted-foreground text-xs">
                The label shown for the link — e.g. "Claim Team History Guide". Display text only;
                it doesn't point at the file.
              </p>
              <Input
                id="claim-team-history-help-file-name"
                value={form.claimTeamHistoryHelpFileName}
                onChange={(e) =>
                  setEdits({ ...edits, claimTeamHistoryHelpFileName: e.target.value })
                }
                placeholder="claim-team-history-help.pdf"
              />
            </div>
            <div className="grid gap-1.5">
              <label className="font-medium text-sm" htmlFor="claim-team-history-help-file">
                Help File Reference
              </label>
              <p className="text-muted-foreground text-xs">
                Where the file actually lives — a web link (URL) to the PDF the label opens.
              </p>
              <Input
                id="claim-team-history-help-file"
                value={form.claimTeamHistoryHelpFile}
                onChange={(e) => setEdits({ ...edits, claimTeamHistoryHelpFile: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
